import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { productUrl } from '../lib/productUrl';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { productsApi, quotesApi, deliveryApi, formatApiError } from '../lib/api';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { format, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, Plus, Minus, Trash2, ShoppingCart, ArrowRight, AlertCircle, Check, Truck, PackageOpen, User, MessageSquare, ChevronDown, Hash, Copy, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ZoneMapModal from '../components/ZoneMapModal';
import AddressAutocomplete from '../components/AddressAutocomplete';
import FastGripLogo from '../components/FastGripLogo';
import { computeKitPricing } from '../lib/fastgrip-pricing';
import { isSaleItem, splitCartItems, sumLineTotal, sumInsurableLineTotal, sumCoefAppliedLineTotal, sumCoefExemptLineTotal, isVehicleItem } from '../lib/saleItems';

// ─── Compact Transport Service Block ─────────────────────────────────
const TransportServiceBlock = ({ label, icon: Icon, enabled, onToggle, config, options, onChange, disabled, disabledReason, address, onAddressChange, onAddressSelect, totalPrice, onCopyFromDelivery, dateValue, onDateChange, dateDisabledFn, datePlaceholder }) => {
  const { t } = useTranslation();
  if (disabled) {
    return (
      <div className="p-4 bg-muted/30 border border-dashed border-border">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="w-5 h-5" />
          <span className="font-semibold">{label}</span>
          <span className="text-xs ml-auto bg-muted px-2 py-0.5">{disabledReason}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border" data-testid={`transport-${label.toLowerCase()}`}>
      <div className={`flex items-center justify-between p-4 transition-colors bg-gray-800 text-white`}>
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" />
          <span className="font-semibold">{label}</span>
          {!enabled && (
            <span className="text-xs italic text-gray-300 ml-1">{t('cart.transport_from', { price: '65' })}</span>
          )}
          {enabled && totalPrice !== null && (
            <span className={`ml-2 text-sm font-semibold text-yellow-300`} data-testid={`total-${label.toLowerCase()}`}>
              {totalPrice === -1 ? t('cart.quote_only', 'Sur devis') : `${totalPrice.toFixed(2)} EUR`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm text-gray-300">{enabled ? 'Oui' : 'Non'}</Label>
          <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`toggle-${label.toLowerCase()}`} />
        </div>
      </div>

      {enabled && (
        <div className="p-4 space-y-4">
          {/* Copy from delivery button */}
          {onCopyFromDelivery && (
            <button
              type="button"
              onClick={onCopyFromDelivery}
              className="text-xs font-medium text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
              data-testid="copy-from-delivery-btn"
            >
              Copier l'adresse, le type de dépose et le contact de la livraison
            </button>
          )}
          {/* Address */}
          <div className="space-y-2">
            <Label className="text-sm">Adresse</Label>
            <AddressAutocomplete
              value={address}
              onChange={onAddressChange}
              onAddressSelect={onAddressSelect}
              placeholder="Saisissez l'adresse..."
            />
          </div>

          {/* Date */}
          {onDateChange && (
            <div className="space-y-2">
              <Label className="text-sm">Date souhaitée</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal rounded-none"
                    data-testid={`date-${label.toLowerCase()}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateValue ? format(dateValue, 'EEEE d MMMM yyyy', { locale: fr }) : (datePlaceholder || 'Sélectionner une date')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateValue}
                    onSelect={onDateChange}
                    disabled={dateDisabledFn}
                    locale={fr}
                    weekStartsOn={1}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Zone</Label>
              <Select value={options.zone} onValueChange={(v) => onChange('zone', v)}>
                <SelectTrigger className="rounded-none" data-testid={`zone-${label.toLowerCase()}`}>
                  <SelectValue placeholder="Sélectionner une zone" />
                </SelectTrigger>
                <SelectContent>
                  {config.zones?.map((z) => (
                    <SelectItem key={z.name} value={z.name}>
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: z.color }} />
                        {z.name}
                        {z.surcharge === -1 ? (
                          <span className="text-orange-500 text-xs ml-1">(sur devis)</span>
                        ) : z.surcharge > 0 ? (
                          <span className="text-muted-foreground text-xs ml-1">(+{z.surcharge} EUR)</span>
                        ) : null}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Créneau horaire</Label>
              <Select value={options.time_slot} onValueChange={(v) => onChange('time_slot', v)}>
                <SelectTrigger className="rounded-none" data-testid={`timeslot-${label.toLowerCase()}`}>
                  <SelectValue placeholder="Sélectionner un créneau" />
                </SelectTrigger>
                <SelectContent>
                  {config.time_slots?.map((ts) => (
                    <SelectItem key={ts.label} value={ts.label}>
                      {ts.label} {ts.surcharge > 0 ? `(+${ts.surcharge} EUR)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type de dépose</Label>
              <Select value={options.delivery_type} onValueChange={(v) => onChange('delivery_type', v)}>
                <SelectTrigger className="rounded-none" data-testid={`type-${label.toLowerCase()}`}>
                  <SelectValue placeholder="Choisir..." />
                </SelectTrigger>
                <SelectContent>
                  {config.delivery_types?.map((dt) => (
                    <SelectItem key={dt.label} value={dt.label}>
                      {dt.label} {dt.surcharge > 0 ? `(+${dt.surcharge} EUR)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Urgence</Label>
              <Select value={options.urgent ? 'urgent' : 'standard'} onValueChange={(v) => onChange('urgent', v === 'urgent')}>
                <SelectTrigger className="rounded-none" data-testid={`urgence-${label.toLowerCase()}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="urgent">Urgente - moins de 2h (+{config.urgency_surcharge || 50} EUR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Kit Cart Item (Grip Kit accordion) ──────────────────────────────
const KitCartItem = ({ item, updateQuantity, removeItem, updateKit }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Update one sub-item of the kit: delta=+1/-1 to change quantity, delta='remove' to delete
  const updateSubItem = (subProductId, delta) => {
    if (!item.kit_items) return;
    let newKitItems = item.kit_items.map(ki => {
      if (ki.product_id !== subProductId) return ki;
      if (delta === 'remove') return null;
      const newQty = (ki.quantity || 0) + delta;
      if (newQty <= 0) return null;
      return { ...ki, quantity: newQty };
    }).filter(Boolean);

    const { total } = computeKitPricing(newKitItems);
    updateKit(item.product_id, {
      kit_items: newKitItems,
      daily_price: Number(total.toFixed(2)),
    });
  };

  return (
    <div className="pb-3 border-b border-border last:border-0 last:pb-0">
      <div className="flex gap-3">
        <div className="w-16 h-14 bg-surface flex items-center justify-center flex-shrink-0">
          {item.image_url ? (
            <img src={item.image_url} alt={item.product_name} className="w-full h-full object-contain" />
          ) : (
            <FastGripLogo size="xs" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 font-medium text-sm hover:text-primary transition-colors w-full text-left"
            data-testid="kit-toggle"
          >
            <FastGripLogo size="sm" />
            <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
          <p className="text-xs text-primary mt-0.5">{item.daily_price.toFixed(2)} EUR/jour</p>
        </div>
        <div className="flex flex-col items-end justify-between gap-1">
          <button onClick={() => removeItem(item.product_id)} className="text-muted-foreground hover:text-red-500 transition-colors" data-testid="kit-remove-btn"><Trash2 className="w-3.5 h-3.5" /></button>
          <Link to="/kit-grip?edit=true" className="text-xs text-primary hover:underline" data-testid="kit-edit-btn">{t('cart.edit')}</Link>
        </div>
      </div>
      {expanded && item.kit_items && (
        <div className="mt-2 ml-4 pl-3 border-l-2 border-border space-y-1.5">
          {item.kit_items.map((ki, idx) => (
            <div key={`${ki.product_id}-${idx}`} className="flex items-center justify-between gap-2 text-xs" data-testid={`kit-subitem-${ki.product_id}`}>
              <span className="flex-1 truncate text-muted-foreground">{ki.product_name}</span>
              <div className="flex items-center border border-border bg-white">
                <button
                  onClick={() => updateSubItem(ki.product_id, -1)}
                  className="px-1.5 py-0.5 hover:bg-surface transition-colors"
                  aria-label="diminuer"
                  data-testid={`kit-subitem-decrease-${ki.product_id}`}
                >
                  <Minus className="w-3 h-3" />
                </button>
                <span className="px-2 font-semibold min-w-[24px] text-center text-foreground">{ki.quantity}</span>
                <button
                  onClick={() => updateSubItem(ki.product_id, +1)}
                  className="px-1.5 py-0.5 hover:bg-surface transition-colors"
                  aria-label="augmenter"
                  data-testid={`kit-subitem-increase-${ki.product_id}`}
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <span className="w-16 text-right text-muted-foreground tabular-nums">{(ki.daily_price * ki.quantity).toFixed(2)} EUR</span>
              <button
                onClick={() => updateSubItem(ki.product_id, 'remove')}
                className="text-muted-foreground hover:text-red-500 transition-colors"
                aria-label="supprimer"
                data-testid={`kit-subitem-remove-${ki.product_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main CartPage ───────────────────────────────────────────────────
const CartPage = () => {
  const cartCtx = useCart();
  const { cart, updateQuantity, removeItem, updateKit, setDates, clearCart: clearCartCtx, addItem: addItemCtx, franchiseChoice, setFranchiseChoice } = cartCtx;
  const { isAuthenticated, user } = useAuth();
  const { t, i18n } = useTranslation();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Restore saved form state from localStorage
  const savedForm = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('pzv_cart_form');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  // Dates
  const [pickupDate, setPickupDate] = useState(() => savedForm?.pickupDate ? new Date(savedForm.pickupDate) : null);
  const [pickupTime, setPickupTime] = useState(savedForm?.pickupTime || '');
  const [returnDate, setReturnDate] = useState(() => savedForm?.returnDate ? new Date(savedForm.returnDate) : null);
  const [returnTime, setReturnTime] = useState(savedForm?.returnTime || '');
  const [message, setMessage] = useState(savedForm?.message || '');
  const [multiplier, setMultiplier] = useState(null);
  const [days, setDays] = useState(1);
  const [calendarDays, setCalendarDays] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);  // { client_email_sent, planning_email_sent, planning_email_to, mailto_link, mailto_body }
  const [holidays, setHolidays] = useState([]);

  // Fetch holidays for calendar
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/holidays`)
      .then(r => r.json())
      .then(data => setHolidays((data.holidays || []).map(h => new Date(h + 'T00:00:00'))))
      .catch(() => {});
  }, []);

  const isDateDisabled = useCallback((date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    const day = date.getDay();
    if (day === 0 || day === 6) return true; // weekend
    return holidays.some(h => h.getFullYear() === date.getFullYear() && h.getMonth() === date.getMonth() && h.getDate() === date.getDate());
  }, [holidays]);

  const isReturnDateDisabled = useCallback((date) => {
    if (pickupDate && date < pickupDate) return true;
    const day = date.getDay();
    if (day === 0 || day === 6) return true;
    return holidays.some(h => h.getFullYear() === date.getFullYear() && h.getMonth() === date.getMonth() && h.getDate() === date.getDate());
  }, [pickupDate, holidays]);

  // Contact info — restore from saved form or user profile
  const [contactFullName, setContactFullName] = useState(savedForm?.contactFullName || user?.name || '');
  const [contactEmail, setContactEmail] = useState(savedForm?.contactEmail || user?.email || '');
  const [contactPhone, setContactPhone] = useState(savedForm?.contactPhone || user?.phone || '');
  const [contactAddress, setContactAddress] = useState(savedForm?.contactAddress || '');
  const [contactCompany, setContactCompany] = useState(savedForm?.contactCompany || user?.company || '');

  // Transport config
  const [deliveryConfig, setDeliveryConfig] = useState(null);

  // Livraison state — restore
  const [livraisonEnabled, setLivraisonEnabled] = useState(savedForm?.livraisonEnabled || false);
  const [livraisonOpts, setLivraisonOpts] = useState(savedForm?.livraisonOpts || { zone: '', time_slot: '', delivery_type: '', urgent: false, _result: null });
  const [livraisonAddress, setLivraisonAddress] = useState(savedForm?.livraisonAddress || '');

  // Récupération state — restore
  const [recuperationEnabled, setRecuperationEnabled] = useState(savedForm?.recuperationEnabled || false);
  const [recuperationOpts, setRecuperationOpts] = useState(savedForm?.recuperationOpts || { zone: '', time_slot: '', delivery_type: '', urgent: false, _result: null });
  const [recuperationAddress, setRecuperationAddress] = useState(savedForm?.recuperationAddress || '');
  const [livraisonDate, setLivraisonDate] = useState(() => savedForm?.livraisonDate ? new Date(savedForm.livraisonDate) : null);
  const [recuperationDate, setRecuperationDate] = useState(() => savedForm?.recuperationDate ? new Date(savedForm.recuperationDate) : null);
  const [dateAdjustDialog, setDateAdjustDialog] = useState(null); // { source, oldPickup, newPickup, oldReturn, newReturn }

  // Contact on site — restore
  const [contactSiteName, setContactSiteName] = useState(savedForm?.contactSiteName || '');
  const [contactSitePhone, setContactSitePhone] = useState(savedForm?.contactSitePhone || '');

  // CUP (Code Unique de Panier)
  const [cpuCode, setCpuCode] = useState(cart.cpu_code || null);
  const [cpuLoading, setCpuLoading] = useState(false);
  const [cpuInput, setCpuInput] = useState('');
  const [cpuLoadError, setCpuLoadError] = useState('');
  const [cpuPreview, setCpuPreview] = useState(null);
  const [cpuCopied, setCpuCopied] = useState(false);

  // Reset CUP when cart changes
  useEffect(() => {
    if (cart.cpu_code !== cpuCode) setCpuCode(cart.cpu_code || null);
  }, [cart.cpu_code]);

  const generateCpu = async () => {
    setCpuLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/cpu`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cart.items })
      });
      const data = await res.json();
      if (res.ok) {
        setCpuCode(data.code);
        // Store in cart context so it persists
        const updated = { ...cart, cpu_code: data.code };
        localStorage.setItem('puzzle_video_cart', JSON.stringify(updated));
      }
    } catch (e) {
      console.error('CPU generation failed:', e);
    } finally {
      setCpuLoading(false);
    }
  };

  const loadCpuCart = async (code) => {
    if (!code || code.length < 4) return;
    setCpuLoadError('');
    setCpuLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/cpu/${code.toUpperCase()}`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        setCpuPreview(data);
      } else {
        setCpuLoadError(data.detail || 'Code introuvable');
        setCpuPreview(null);
      }
    } catch {
      setCpuLoadError('Erreur de connexion');
    } finally {
      setCpuLoading(false);
    }
  };

  const mergeCpuToCart = () => {
    if (!cpuPreview?.items) return;
    for (const item of cpuPreview.items) {
      // FastGrip kits are pushed as a SINGLE item with their `kit_items`
      // intact (one push per kit, not one per quantity, mirroring addItem's
      // kit branch which doesn't merge identical kits anyway).
      if (item.is_kit) {
        addItemCtx({
          id: item.product_id,
          name: item.product_name,
          sku: item.product_sku,
          daily_price: item.daily_price,
          image_url: item.image_url,
          is_kit: true,
          kit_items: item.kit_items || [],
        }, item.quantity || 1);
        continue;
      }
      // Cross-sell items must keep their parent linkage / original price so
      // the cart still recognises them as add-ons of the parent.
      if (item.is_cross_sell) {
        addItemCtx({
          id: item.product_id,
          name: item.product_name,
          sku: item.product_sku,
          daily_price: item.daily_price,
          image_url: item.image_url,
          is_cross_sell: true,
          original_price: item.original_price || item.daily_price,
          parent_product_id: item.parent_product_id || null,
        }, item.quantity || 1);
        continue;
      }
      // Plain rental items: add one by one to keep the legacy behaviour
      // (each addItem increments quantity if the same product already exists).
      for (let i = 0; i < (item.quantity || 1); i++) {
        addItemCtx({
          id: item.product_id,
          name: item.product_name,
          sku: item.product_sku,
          daily_price: item.daily_price,
          image_url: item.image_url
        });
      }
    }
    setCpuPreview(null);
    setCpuInput('');
  };
  // Zone detection helper
  const detectZone = async (postalCode, setOpts) => {
    if (!postalCode) return;
    try {
      const res = await deliveryApi.getZoneFromAddress(postalCode);
      setOpts(prev => ({ ...prev, zone: res.data.zone }));
    } catch {}
  };

  // Fetch delivery config
  useEffect(() => {
    deliveryApi.getConfig().then(res => {
      const cfg = res.data;
      setDeliveryConfig(cfg);
      // Preload zone map image
      if (cfg.zone_map_url) {
        const img = new Image();
        img.src = cfg.zone_map_url;
      }
    }).catch(() => {});
  }, []);

  // Check if all date fields are filled
  const datesComplete = pickupDate && returnDate && pickupTime && returnTime;

  // Fetch multiplier when all dates are set - updates in real-time
  useEffect(() => {
    if (!datesComplete) { setMultiplier(null); return; }
    setLoading(true);
    productsApi.getMultiplier(format(pickupDate, 'yyyy-MM-dd'), format(returnDate, 'yyyy-MM-dd'), pickupTime, returnTime)
      .then(res => {
        setMultiplier(res.data.multiplier);
        setDays(res.data.days);
        setCalendarDays(res.data.calendar_days || res.data.days);
      })
      .catch(() => setMultiplier(1))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDate, returnDate, pickupTime, returnTime]);

  // Sync dates to cart context separately to avoid re-triggering multiplier fetch
  useEffect(() => {
    if (datesComplete) {
      setDates(format(pickupDate, 'yyyy-MM-dd'), pickupTime, format(returnDate, 'yyyy-MM-dd'), returnTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDate, returnDate, pickupTime, returnTime]);

  // Compute delivery costs
  const calcTransport = useCallback(async (type, enabled, opts, setOpts) => {
    if (!enabled) { setOpts(prev => ({ ...prev, _result: null })); return; }
    try {
      const res = await deliveryApi.calculate({
        enabled: true, type, zone: opts.zone, time_slot: opts.time_slot,
        delivery_type: opts.delivery_type, urgent: opts.urgent
      });
      setOpts(prev => ({ ...prev, _result: res.data }));
    } catch { setOpts(prev => ({ ...prev, _result: null })); }
  }, []);

  useEffect(() => { calcTransport('livraison', livraisonEnabled, livraisonOpts, setLivraisonOpts); },
    [livraisonEnabled, livraisonOpts.zone, livraisonOpts.time_slot, livraisonOpts.delivery_type, livraisonOpts.urgent, calcTransport]);

  useEffect(() => { calcTransport('recuperation', recuperationEnabled, recuperationOpts, setRecuperationOpts); },
    [recuperationEnabled, recuperationOpts.zone, recuperationOpts.time_slot, recuperationOpts.delivery_type, recuperationOpts.urgent, calcTransport]);

  // Pricing — sale items (SKU "vente*") are flat-priced (no multiplier, no insurance).
  // Vehicles ("cam20*", "cam12*") and their options (camcond, camfranp, camfrant)
  // are rentals BUT bill linearly on business days (no day-coefficient). They are
  // also exempted from the 8% insurance (own franchise scheme).
  // Parking spots ("park*") are exempted from insurance but still coefficient-based.
  const { rentals: rentalItems, sales: saleItems, vehicles: vehicleItems } = splitCartItems(cart.items);
  const rentalDailyTotal = sumLineTotal(rentalItems);
  const saleTotal = sumLineTotal(saleItems); // flat price * qty
  const coefAppliedDaily = sumCoefAppliedLineTotal(rentalItems);
  const coefExemptDaily  = sumCoefExemptLineTotal(rentalItems);
  const rentalSubtotal = datesComplete
    ? (coefAppliedDaily * (multiplier || 1) + coefExemptDaily * (calendarDays || 1))
    : 0;
  // Insurance applies only to insurance-eligible rentals (excludes vehicles + parking)
  const insurableDaily = sumInsurableLineTotal(rentalItems);
  const insurableSubtotal = datesComplete ? insurableDaily * (multiplier || 1) : 0;
  const subtotal = rentalSubtotal + saleTotal;
  const insurance = insurableSubtotal * 0.08; // 8% on insurance-eligible rentals only

  // Vehicle franchise buyback options (only when at least one vehicle is in cart)
  const hasVehicleItem = vehicleItems.length > 0;
  const FRANCHISE_OPTIONS = useMemo(() => ({
    partial: { id: 'partial', sku: 'camfranp', label_fr: 'Rachat partiel', label_en: 'Partial waiver', daily: 19, fees: 200,
               franchise_max: 1000, majoration: 1000 },
    total:   { id: 'total',   sku: 'camfrant', label_fr: 'Rachat total',   label_en: 'Full waiver',    daily: 29, fees: 200,
               franchise_max: 0,    majoration: 1000 },
  }), []);
  useEffect(() => { if (!hasVehicleItem && franchiseChoice) setFranchiseChoice(null); }, [hasVehicleItem, franchiseChoice, setFranchiseChoice]);
  const franchiseOpt = franchiseChoice ? FRANCHISE_OPTIONS[franchiseChoice] : null;
  const franchiseTotal = (hasVehicleItem && franchiseOpt && datesComplete)
    ? (franchiseOpt.daily * (calendarDays || 1) + franchiseOpt.fees) : 0;

  const livraisonTotal = livraisonOpts._result?.enabled && !livraisonOpts._result?.sur_devis ? livraisonOpts._result.total : 0;
  const recuperationTotal = recuperationOpts._result?.enabled && !recuperationOpts._result?.sur_devis ? recuperationOpts._result.total : 0;
  const total = subtotal + insurance + franchiseTotal + livraisonTotal + recuperationTotal;

  // Vehicle detection (legacy heuristic used by delivery widget — keep working)
  const vehicleSkus = deliveryConfig?.vehicle_skus || ['cam20', 'cam12'];
  const hasVehicle = hasVehicleItem || cart.items.some(item => vehicleSkus.some(prefix => item.product_sku?.toLowerCase().startsWith(prefix)));

  // Threshold check — delivery is now always allowed, even with a vehicle in the cart
  const deliveryThreshold = deliveryConfig?.threshold || 1;
  const transportAvailable = deliveryConfig?.enabled && rentalDailyTotal >= deliveryThreshold;

  useEffect(() => {
    if (!transportAvailable) {
      setLivraisonEnabled(false);
      setRecuperationEnabled(false);
    }
  }, [transportAvailable]);

  const needContactOnSite = livraisonEnabled || recuperationEnabled;

  // Persist form data to localStorage on every change
  useEffect(() => {
    const formData = {
      pickupDate: pickupDate ? pickupDate.toISOString() : null,
      pickupTime, returnDate: returnDate ? returnDate.toISOString() : null, returnTime,
      message, contactFullName, contactEmail, contactPhone, contactAddress, contactCompany,
      livraisonEnabled, livraisonOpts: { ...livraisonOpts, _result: null }, livraisonAddress,
      livraisonDate: livraisonDate ? livraisonDate.toISOString() : null,
      recuperationEnabled, recuperationOpts: { ...recuperationOpts, _result: null }, recuperationAddress,
      recuperationDate: recuperationDate ? recuperationDate.toISOString() : null,
      contactSiteName, contactSitePhone
    };
    localStorage.setItem('pzv_cart_form', JSON.stringify(formData));
  }, [pickupDate, pickupTime, returnDate, returnTime, message,
    contactFullName, contactEmail, contactPhone, contactAddress, contactCompany,
    livraisonEnabled, livraisonOpts.zone, livraisonOpts.time_slot, livraisonOpts.delivery_type, livraisonOpts.urgent,
    livraisonAddress, livraisonDate,
    recuperationEnabled, recuperationOpts.zone, recuperationOpts.time_slot,
    recuperationOpts.delivery_type, recuperationOpts.urgent, recuperationAddress, recuperationDate,
    contactSiteName, contactSitePhone]);

  // Auto-adjust rental dates when delivery is earlier than pickup, or
  // recuperation is later than return. Fires ONLY when both the date AND
  // the time_slot are filled for the corresponding widget (in either order).
  // The client always pays for the extra days when the equipment is
  // immobilised in his hands.
  useEffect(() => {
    if (!pickupDate || !returnDate) return;
    const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const pk = stripTime(pickupDate);
    const rt = stripTime(returnDate);

    const livraisonReady = livraisonEnabled && livraisonDate && !!livraisonOpts.time_slot;
    const recupReady     = recuperationEnabled && recuperationDate && !!recuperationOpts.time_slot;

    const dv = livraisonReady ? stripTime(livraisonDate) : null;
    const rc = recupReady     ? stripTime(recuperationDate) : null;

    const needAdvancePickup = dv && dv < pk;
    const needPostponeReturn = rc && rc > rt;
    if (!needAdvancePickup && !needPostponeReturn) return;

    const oldPickup = pickupDate;
    const oldReturn = returnDate;
    if (needAdvancePickup) setPickupDate(livraisonDate);
    if (needPostponeReturn) setReturnDate(recuperationDate);

    const source = needAdvancePickup && needPostponeReturn
      ? 'both' : needAdvancePickup ? 'livraison' : 'recuperation';
    setDateAdjustDialog({
      source,
      oldPickup, newPickup: needAdvancePickup ? livraisonDate : oldPickup,
      oldReturn, newReturn: needPostponeReturn ? recuperationDate : oldReturn,
    });
  }, [livraisonDate, recuperationDate, livraisonEnabled, recuperationEnabled,
      livraisonOpts.time_slot, recuperationOpts.time_slot, pickupDate, returnDate]);


  // Clear form data helper
  const clearFormData = useCallback(() => {
    localStorage.removeItem('pzv_cart_form');
  }, []);

  // Wrap clearCart to also clear form data
  const clearCart = useCallback(() => {
    clearFormData();
    clearCartCtx();
  }, [clearFormData, clearCartCtx]);

  const getLivraisonPrice = () => {
    if (!livraisonOpts._result?.enabled) return null;
    return livraisonOpts._result.sur_devis ? -1 : livraisonOpts._result.total;
  };
  const getRecuperationPrice = () => {
    if (!recuperationOpts._result?.enabled) return null;
    return recuperationOpts._result.sur_devis ? -1 : recuperationOpts._result.total;
  };

  // Copy livraison info to récupération (address, zone, delivery_type) but reset time_slot and urgent
  const copyDeliveryToRecuperation = () => {
    setRecuperationAddress(livraisonAddress);
    setRecuperationOpts(prev => ({
      ...prev,
      zone: livraisonOpts.zone,
      delivery_type: livraisonOpts.delivery_type,
      time_slot: '',
      urgent: false
    }));
    setContactSiteName(prev => prev || contactSiteName);
    setContactSitePhone(prev => prev || contactSitePhone);
  };

  const handleSubmitQuote = async () => {
    if (!pickupDate || !returnDate || !pickupTime || !returnTime) { setError('Veuillez renseigner les dates et les plages horaires'); return; }
    if (cart.items.length === 0) { setError(t('cart.empty_cart_title')); return; }
    if (!contactFullName.trim()) { setError('Le nom est obligatoire'); return; }
    if (!contactEmail.trim()) { setError("L'email est obligatoire"); return; }
    if (!contactPhone.trim()) { setError('Le téléphone est obligatoire'); return; }
    if (!contactAddress.trim()) { setError("L'adresse est obligatoire"); return; }

    setSubmitting(true);
    setError('');

    const livraisonData = livraisonEnabled ? {
      enabled: true, type: 'livraison', zone: livraisonOpts.zone,
      time_slot: livraisonOpts.time_slot, delivery_type: livraisonOpts.delivery_type,
      urgent: livraisonOpts.urgent, address: livraisonAddress,
      date: livraisonDate ? format(livraisonDate, 'yyyy-MM-dd') : null,
    } : null;

    const recuperationData = recuperationEnabled ? {
      enabled: true, type: 'recuperation', zone: recuperationOpts.zone,
      time_slot: recuperationOpts.time_slot, delivery_type: recuperationOpts.delivery_type,
      urgent: recuperationOpts.urgent, address: recuperationAddress,
      date: recuperationDate ? format(recuperationDate, 'yyyy-MM-dd') : null,
    } : null;

    // Compute discounts from cross-sell accessories (10% off)
    const discountsList = [];
    for (const item of cart.items) {
      if (item.is_cross_sell && item.original_price && item.original_price > item.daily_price) {
        discountsList.push({
          ref: item.product_sku || item.product_id,
          amount: parseFloat(((item.original_price - item.daily_price) * item.quantity).toFixed(2)),
          reason: `Remise accessoire cross-sell -10% sur ${item.product_name}`
        });
      }
    }

    const quotePayload = {
      name: contactFullName,
      email: contactEmail,
      phone: contactPhone,
      address: contactAddress,
      company: contactCompany || null,
      items: cart.items,
      pickup_date: format(pickupDate, 'yyyy-MM-dd'), pickup_time: pickupTime,
      return_date: format(returnDate, 'yyyy-MM-dd'), return_time: returnTime,
      message: message || null,
      livraison: livraisonData,
      recuperation: recuperationData,
      contact_on_site_name: contactSiteName || null,
      contact_on_site_phone: contactSitePhone || null,
      lang: i18n.language || 'fr',
      discounts: discountsList.length > 0 ? discountsList : null,
      // Vehicle franchise buyback chosen (if any)
      vehicle_franchise: franchiseOpt ? {
        choice: franchiseOpt.id,           // 'partial' | 'total'
        sku: franchiseOpt.sku,             // 'camfranp' | 'camfrant'
        label: franchiseOpt.label_fr,
        daily_price: franchiseOpt.daily,
        fees: franchiseOpt.fees,
        days: calendarDays || 1,
        franchise_max: franchiseOpt.franchise_max,
        majoration: franchiseOpt.majoration,
        total: parseFloat(franchiseTotal.toFixed(2)),
      } : null,
    };

    try {
      let res;
      if (isAuthenticated) {
        res = await quotesApi.createQuoteRequest(quotePayload);
      } else {
        res = await quotesApi.createGuestQuoteRequest(quotePayload);
      }
      const data = res?.data || {};

      // Build a plain-text fallback the customer can copy/paste into their
      // own email client if our SMTP failed to deliver the planning email.
      const fmtDate = (d) => d ? format(d, 'dd/MM/yyyy') : '';
      const itemsText = cart.items.map(i =>
        `- ${i.product_name} (ref: ${i.product_sku || '—'}) x${i.quantity} — ${i.daily_price} €/j`
      ).join('\n');
      const mailBody = [
        'Bonjour,',
        '',
        `Je souhaiterais obtenir un devis pour la location suivante :`,
        '',
        `Retrait : ${fmtDate(pickupDate)} (${pickupTime})`,
        `Retour  : ${fmtDate(returnDate)} (${returnTime})`,
        '',
        'Articles :',
        itemsText,
        '',
        `Nom : ${contactFullName}`,
        `Société : ${contactCompany || '—'}`,
        `Email : ${contactEmail}`,
        `Téléphone : ${contactPhone}`,
        `Adresse : ${contactAddress}`,
        ...(livraisonEnabled ? [
          '',
          'LIVRAISON ALLER :',
          `  Adresse : ${livraisonAddress}`,
          `  Zone : ${livraisonOpts.zone}`,
          `  Créneau : ${livraisonOpts.time_slot}`,
          `  Mode : ${livraisonOpts.delivery_type}`,
          `  Urgent : ${livraisonOpts.urgent ? 'OUI' : 'non'}`,
        ] : []),
        ...(recuperationEnabled ? [
          '',
          'RÉCUPÉRATION RETOUR :',
          `  Adresse : ${recuperationAddress}`,
          `  Zone : ${recuperationOpts.zone}`,
          `  Créneau : ${recuperationOpts.time_slot}`,
          `  Mode : ${recuperationOpts.delivery_type}`,
          `  Urgent : ${recuperationOpts.urgent ? 'OUI' : 'non'}`,
        ] : []),
        ...((livraisonEnabled || recuperationEnabled) && contactSiteName ? [
          '',
          `Contact sur place : ${contactSiteName} (${contactSitePhone})`,
        ] : []),
        ...(message ? ['', `Message : ${message}`] : []),
        '',
        'Merci d\'avance.',
      ].join('\n');

      // Email recipient comes from the backend (server-side env), never hardcoded
      // client-side to avoid scraping.
      const planningTo = data.planning_email_to || '';
      const mailtoLink = planningTo
        ? `mailto:${planningTo}?subject=${encodeURIComponent('Demande de devis - ' + contactFullName)}&body=${encodeURIComponent(mailBody)}`
        : '';

      setSuccessData({
        client_email_sent: data.client_email_sent !== false,
        planning_email_sent: data.planning_email_sent !== false,
        planning_email_to: planningTo,
        mailto_link: mailtoLink,
        mail_body: mailBody,
      });
      setSuccess(true);
      // Reset dates so the next request starts fresh.
      setPickupDate(null);
      setPickupTime('');
      setReturnDate(null);
      setReturnTime('');
      // Scroll to top so the user actually sees the success banner.
      window.scrollTo({ top: 0, behavior: 'auto' });
      clearCart();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Une erreur s'est produite");
    } finally {
      setSubmitting(false);
    }
  };

  const recapRef = useRef(null);
  const [recapVisible, setRecapVisible] = useState(true);

  useEffect(() => {
    if (!recapRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setRecapVisible(entry.isIntersecting),
      { threshold: 0.05 }
    );
    observer.observe(recapRef.current);
    return () => observer.disconnect();
  }, [cart.items.length]);

  if (success) {
    const planningSent = successData?.planning_email_sent !== false;
    return (
      <div className="min-h-screen py-16" data-testid="cart-success">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mx-auto text-center">
            <div className={`w-16 h-16 ${planningSent ? 'bg-green-100' : 'bg-amber-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
              <Check className={`w-8 h-8 ${planningSent ? 'text-green-600' : 'text-amber-600'}`} />
            </div>
            <h1 className="text-3xl font-extrabold mb-4">{t('cart.success_title')}</h1>
            <p className="text-muted-foreground mb-8">
              {t('cart.success_desc')}
            </p>

            {!planningSent && successData && (
              <div className="border border-amber-300 bg-amber-50 p-5 mb-8 text-left" data-testid="email-fallback-panel">
                <h2 className="text-base font-bold text-amber-900 mb-2">
                  ⚠️ L&apos;envoi automatique à notre planning a échoué
                </h2>
                <p className="text-sm text-amber-900 mb-4">
                  Votre demande est bien enregistrée dans notre système, mais l&apos;email
                  vers <strong>{successData.planning_email_to}</strong> n&apos;a pas pu partir.
                  Pour que votre demande soit traitée rapidement, envoyez-la directement
                  depuis votre boîte mail :
                </p>

                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                  <a
                    href={successData.mailto_link}
                    className="inline-flex items-center justify-center px-4 py-2 bg-amber-900 text-white text-sm font-semibold hover:bg-amber-800 transition-colors"
                    data-testid="open-mailto-btn"
                  >
                    Ouvrir mon client mail
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(successData.mail_body || '');
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 border border-amber-900 text-amber-900 text-sm font-semibold hover:bg-amber-100 transition-colors"
                    data-testid="copy-mailbody-btn"
                  >
                    Copier le récapitulatif
                  </button>
                </div>

                <p className="text-xs text-amber-800 mb-2">
                  Destinataire : <strong>{successData.planning_email_to}</strong>
                </p>
                <textarea
                  readOnly
                  value={successData.mail_body || ''}
                  rows={10}
                  className="w-full border border-amber-300 bg-white p-3 text-xs font-mono leading-relaxed"
                  data-testid="mailbody-textarea"
                />
              </div>
            )}

            <Link to="/catalogue">
              <Button variant="outline" className="w-full rounded-none" data-testid="back-to-catalogue-btn">{t('cart.back_to_catalogue')}</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen py-16" data-testid="cart-empty">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <ShoppingCart className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
          <h1 className="text-3xl font-extrabold mb-4">{t('cart.empty_cart_title')}</h1>
          <p className="text-muted-foreground mb-8">{t('cart.empty_desc')}</p>

          {/* CUP input on empty cart */}
          <div className="max-w-sm mx-auto mb-8">
            <p className="text-sm font-semibold mb-2">Vous avez un Code Unique de Panier (CUP) ?</p>
            <div className="flex gap-2">
              <Input
                value={cpuInput}
                onChange={(e) => setCpuInput(e.target.value.toUpperCase())}
                placeholder="Ex: A1B2C3D4"
                className="rounded-none font-mono uppercase"
                maxLength={8}
                data-testid="cpu-input-empty"
              />
              <Button className="rounded-none" onClick={() => loadCpuCart(cpuInput)} disabled={cpuLoading || cpuInput.length < 4} data-testid="cpu-load-btn-empty">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            {cpuLoadError && <p className="text-xs text-red-600 mt-1">{cpuLoadError}</p>}
            {cpuPreview && (
              <div className="mt-3 border border-border p-3 text-left text-sm space-y-2">
                <p className="font-semibold">{cpuPreview.items.length} article(s) trouvé(s)</p>
                {cpuPreview.items.slice(0, 5).map((item, i) => {
                  const isUnavailable = item.available === false;
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {item.image_url && <img src={item.image_url} alt="" className={`w-8 h-8 object-contain ${isUnavailable ? 'opacity-50' : ''}`} />}
                      <div className="flex-1">
                        <div className={isUnavailable ? 'text-red-600 font-medium' : ''}>{item.product_name}</div>
                        {isUnavailable && (
                          <div className="text-[10px] italic text-red-600">Ce produit n'est plus référencé</div>
                        )}
                      </div>
                      <span className="text-muted-foreground">x{item.quantity}</span>
                    </div>
                  );
                })}
                {cpuPreview.items.length > 5 && <p className="text-xs text-muted-foreground">+{cpuPreview.items.length - 5} autre(s)</p>}
                <Button className="rounded-none w-full mt-2" size="sm" onClick={mergeCpuToCart} data-testid="cpu-merge-btn-empty">
                  Charger ce panier
                </Button>
              </div>
            )}
          </div>

          <Link to="/catalogue">
            <Button className="rounded-none">{t('cart.view_catalogue')} <ArrowRight className="ml-2 w-4 h-4" /></Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="cart-page">
      <SEO title="Panier" description="Votre panier de location — finalisez vos dates et demandez un devis gratuit. Puzzle Video, location de matériel audiovisuel professionnel." noindex />
      {/* ── Mini price bar — bottom on mobile (above tab bar), top under header on desktop ── */}
      {!recapVisible && cart.items.length > 0 && (
        <div className="fixed left-0 right-0 z-[55] bg-white border-b border-border shadow-sm bottom-0 lg:bottom-auto lg:top-[88px] lg:border-b border-t lg:border-t-0" data-testid="mini-price-bar">
          <div className="container mx-auto px-4 md:px-12 lg:px-24 py-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 text-sm min-w-0">
              <span className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap">{cart.items.length} article{cart.items.length > 1 ? 's' : ''}</span>
              <Separator orientation="vertical" className="h-5 hidden sm:block" />
              <span className="font-bold text-sm sm:text-lg text-primary whitespace-nowrap" data-testid="mini-total">{total.toFixed(2)} EUR HT</span>
            </div>
            <Button onClick={handleSubmitQuote} className="rounded-none bg-primary hover:bg-primary/90 h-8 text-xs px-3 sm:px-4 flex-shrink-0" disabled={submitting || !datesComplete} data-testid="mini-submit-btn">
              <span className="hidden sm:inline">{submitting ? t('cart.submitting') : t('cart.submit')}</span>
              <span className="sm:hidden">{submitting ? '...' : 'Devis'}</span>
              <ArrowRight className="ml-1 w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-2">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">{t('cart.subtitle')}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t('cart.title')}</h1>
        </div>

        {hasVehicleItem && !franchiseChoice && (
          <div
            className="mt-4 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 flex items-start gap-3"
            data-testid="franchise-reminder-banner"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-amber-900">Rachat de franchise véhicule non choisi</p>
              <p className="text-amber-800">
                Votre panier contient un véhicule mais aucune option de rachat de franchise n&apos;a été sélectionnée.
                La franchise standard est de <strong>3&nbsp;000&nbsp;€ HT</strong> en cas de sinistre.
                <a
                  href="#franchise-section"
                  className="ml-1 underline underline-offset-2 hover:text-amber-900"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('franchise-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                >
                  Choisir une option
                </a>
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
          {/* COLONNE GAUCHE */}
          <div className="lg:col-span-7 space-y-4">

            {/* ── DATES ── */}
            <Card className="border border-border rounded-none">
              <CardHeader className="bg-primary text-white rounded-t-lg py-2 px-4 mb-4"><CardTitle className="text-sm flex items-center gap-2 text-white"><CalendarIcon className="w-4 h-4" /> {t('cart.dates_title')}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t('cart.pickup_date')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal rounded-none" data-testid="pickup-date-trigger">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {pickupDate ? format(pickupDate, 'PPP', { locale: fr }) : t('cart.select')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={pickupDate}
                          onSelect={(d) => { setPickupDate(d); if (!returnDate || (d && returnDate < d)) setReturnDate(null); }}
                          disabled={isDateDisabled} initialFocus weekStartsOn={1} locale={fr} />
                      </PopoverContent>
                    </Popover>
                    <Select value={pickupTime} onValueChange={setPickupTime}>
                      <SelectTrigger className="rounded-none" data-testid="pickup-time"><SelectValue placeholder="Plage horaire..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="morning">{t('cart.morning')}</SelectItem>
                        <SelectItem value="afternoon">{t('cart.afternoon')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('cart.return_date')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal rounded-none" data-testid="return-date-trigger">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {returnDate ? format(returnDate, 'PPP', { locale: fr }) : t('cart.select')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={returnDate} onSelect={setReturnDate}
                          disabled={isReturnDateDisabled} initialFocus weekStartsOn={1} locale={fr} />
                      </PopoverContent>
                    </Popover>
                    <Select value={returnTime} onValueChange={setReturnTime}>
                      <SelectTrigger className="rounded-none" data-testid="return-time"><SelectValue placeholder="Plage horaire..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="morning">{t('cart.morning')}</SelectItem>
                        <SelectItem value="afternoon">{t('cart.afternoon')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── TRANSPORT ── */}
            {deliveryConfig?.enabled && (
              <Card className="border border-border rounded-none" data-testid="transport-section">
                <CardHeader className="bg-primary text-white rounded-t-lg py-2 px-4 mb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2 text-white"><Truck className="w-4 h-4" /> Transport</CardTitle>
                    {deliveryConfig.zone_map_url && <ZoneMapModal mapUrl={deliveryConfig.zone_map_url} zones={deliveryConfig.zones} />}
                  </div>
                  {!hasVehicle && !transportAvailable && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {datesComplete ? t('cart.transport_add_more', { threshold: deliveryThreshold }) : t('cart.transport_set_dates')}
                    </p>
                  )}
                </CardHeader>
                {transportAvailable && (
                  <CardContent className="space-y-4">
                    <TransportServiceBlock label="Livraison" icon={Truck} enabled={livraisonEnabled} onToggle={setLivraisonEnabled}
                      config={deliveryConfig} options={livraisonOpts} onChange={(k, v) => setLivraisonOpts(p => ({ ...p, [k]: v }))}
                      disabled={false} address={livraisonAddress} onAddressChange={setLivraisonAddress}
                      onAddressSelect={(a) => detectZone(a.postalCode, setLivraisonOpts)} totalPrice={getLivraisonPrice()}
                      dateValue={livraisonDate} onDateChange={setLivraisonDate}
                      dateDisabledFn={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      datePlaceholder="Date de livraison souhaitée" />
                    <TransportServiceBlock label="Récupération" icon={PackageOpen} enabled={recuperationEnabled} onToggle={setRecuperationEnabled}
                      config={deliveryConfig} options={recuperationOpts} onChange={(k, v) => setRecuperationOpts(p => ({ ...p, [k]: v }))}
                      disabled={false} address={recuperationAddress} onAddressChange={setRecuperationAddress}
                      onAddressSelect={(a) => detectZone(a.postalCode, setRecuperationOpts)} totalPrice={getRecuperationPrice()}
                      onCopyFromDelivery={livraisonEnabled ? copyDeliveryToRecuperation : undefined}
                      dateValue={recuperationDate} onDateChange={setRecuperationDate}
                      dateDisabledFn={(d) => d < new Date(new Date().setHours(0,0,0,0)) || (livraisonDate && d < livraisonDate) || (pickupDate && d < pickupDate)}
                      datePlaceholder="Date de récupération souhaitée" />
                    {needContactOnSite && (
                      <div className="p-4 bg-surface/50 border border-border space-y-3">
                        <p className="text-sm font-semibold">Contact sur place <span className="text-muted-foreground font-normal">(optionnel)</span></p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1"><Label className="text-xs">Nom</Label><Input value={contactSiteName} onChange={(e) => setContactSiteName(e.target.value)} placeholder="Nom" className="rounded-none" data-testid="contact-on-site-name" /></div>
                          <div className="space-y-1"><Label className="text-xs">Téléphone</Label><Input value={contactSitePhone} onChange={(e) => setContactSitePhone(e.target.value)} placeholder="+33 6 00 00 00 00" className="rounded-none" data-testid="contact-on-site-phone" /></div>
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3">{t('cart.dossier_notice')}</p>
                  </CardContent>
                )}
              </Card>
            )}

            {/* ── RACHAT DE FRANCHISE DU VÉHICULE ── */}
            {hasVehicleItem && (
              <Card id="franchise-section" className="border border-border rounded-none" data-testid="franchise-section">
                <CardHeader className="bg-primary text-white rounded-t-lg py-2 px-4 mb-4">
                  <CardTitle className="text-sm flex items-center gap-2 text-white">
                    <Truck className="w-4 h-4" /> Rachat de franchise du véhicule
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Votre panier contient un véhicule. Choisissez une option de rachat de franchise (recommandé) :
                  </p>

                  {/* No selection — required */}
                  <label
                    className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${franchiseChoice === null ? 'border-primary bg-primary/5' : 'border-border hover:bg-gray-50'}`}
                    data-testid="franchise-none"
                  >
                    <input type="radio" checked={franchiseChoice === null} onChange={() => setFranchiseChoice(null)} className="accent-primary" />
                    <span className="text-sm font-medium">Aucun rachat (franchise standard pleine)</span>
                  </label>

                  {/* Partial buyback */}
                  <label
                    className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${franchiseChoice === 'partial' ? 'border-primary bg-primary/5' : 'border-border hover:bg-gray-50'}`}
                    data-testid="franchise-partial"
                  >
                    <input type="radio" checked={franchiseChoice === 'partial'} onChange={() => setFranchiseChoice('partial')} className="accent-primary mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-sm font-semibold">Rachat partiel</span>
                        <span className="text-xs font-mono text-muted-foreground">camfranp</span>
                        <span className="text-sm font-semibold text-primary">19 € HT / jour</span>
                      </div>
                      <ul className="text-[11px] text-muted-foreground mt-1 space-y-0.5 list-disc list-inside">
                        <li>Franchise maximale restant à charge en cas de sinistre&nbsp;: <strong>1&nbsp;000&nbsp;€ HT</strong></li>
                        <li>Majoration de 1&nbsp;000&nbsp;€ HT pour les parties hautes et basses</li>
                        <li>+ 200&nbsp;€ HT de frais de dossier (forfait)</li>
                      </ul>
                    </div>
                  </label>

                  {/* Full buyback */}
                  <label
                    className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${franchiseChoice === 'total' ? 'border-primary bg-primary/5' : 'border-border hover:bg-gray-50'}`}
                    data-testid="franchise-total"
                  >
                    <input type="radio" checked={franchiseChoice === 'total'} onChange={() => setFranchiseChoice('total')} className="accent-primary mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-sm font-semibold">Rachat total</span>
                        <span className="text-xs font-mono text-muted-foreground">camfrant</span>
                        <span className="text-sm font-semibold text-primary">29 € HT / jour</span>
                      </div>
                      <ul className="text-[11px] text-muted-foreground mt-1 space-y-0.5 list-disc list-inside">
                        <li>Franchise maximale restant à charge en cas de sinistre&nbsp;: <strong>0&nbsp;€ HT</strong></li>
                        <li>Majoration de 1&nbsp;000&nbsp;€ HT pour les parties hautes et basses</li>
                        <li>+ 200&nbsp;€ HT de frais de dossier (forfait)</li>
                      </ul>
                    </div>
                  </label>

                  {franchiseOpt && datesComplete && (
                    <div className="bg-primary/5 border border-primary/20 p-3 text-sm flex items-center justify-between" data-testid="franchise-total-row">
                      <span>{franchiseOpt.label_fr} ({franchiseOpt.daily} €/jour × {calendarDays} {calendarDays > 1 ? 'jours' : 'jour'}) + 200 € de frais</span>
                      <strong>{franchiseTotal.toFixed(2)} € HT</strong>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ── COORDONNÉES ── */}
            <Card className="border border-border rounded-none" data-testid="contact-info-section">
              <CardHeader className="bg-primary text-white rounded-t-lg py-2 px-4 mb-4"><CardTitle className="text-sm flex items-center gap-2 text-white"><User className="w-4 h-4" /> {t('cart.your_info')}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label htmlFor="contact-name">{t('cart.name')} *</Label><Input id="contact-name" value={contactFullName} onChange={(e) => setContactFullName(e.target.value)} placeholder="Votre nom complet" required className="rounded-none" data-testid="contact-name" /></div>
                  <div className="space-y-2"><Label htmlFor="contact-company">{t('cart.company')}</Label><Input id="contact-company" value={contactCompany} onChange={(e) => setContactCompany(e.target.value)} placeholder="Société / production (optionnel)" className="rounded-none" data-testid="contact-company" /></div>
                  <div className="space-y-2"><Label htmlFor="contact-email">{t('cart.email')} *</Label><Input id="contact-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="Votre email" required className="rounded-none" data-testid="contact-email" /></div>
                  <div className="space-y-2"><Label htmlFor="contact-phone">{t('cart.phone')} *</Label><Input id="contact-phone" type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Votre téléphone" required className="rounded-none" data-testid="contact-phone" /></div>
                  <div className="md:col-span-2 space-y-2"><Label htmlFor="contact-address">Adresse *</Label><AddressAutocomplete value={contactAddress} onChange={setContactAddress} onAddressSelect={(a) => setContactAddress(a.label)} placeholder="Votre adresse postale" id="contact-address" /></div>
                </div>
              </CardContent>
            </Card>

            {/* ── MESSAGE ── */}
            <Card className="border border-border rounded-none">
              <CardHeader className="bg-primary text-white rounded-t-lg py-2 px-4 mb-4"><CardTitle className="text-sm flex items-center gap-2 text-white"><MessageSquare className="w-4 h-4" /> {t('cart.message_title')}</CardTitle></CardHeader>
              <CardContent>
                <Textarea placeholder={t('cart.message_placeholder')} value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-none min-h-[100px]" data-testid="quote-message" />
              </CardContent>
            </Card>
          </div>

          {/* ══════ COLONNE DROITE ══════ */}
          <div className="lg:col-span-5 space-y-4">
            {/* ── RÉCAPITULATIF ── */}
            <Card className="border border-border rounded-none" data-testid="recap-section" ref={recapRef}>
              <CardHeader><CardTitle className="text-lg">{t('cart.summary')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {!datesComplete ? (
                  <div className="py-4 text-center" data-testid="price-placeholder">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t('cart.price_placeholder')}
                    </p>
                  </div>
                ) : loading ? (
                  <div className="animate-pulse space-y-3"><div className="h-4 bg-gray-200 w-full" /><div className="h-4 bg-gray-200 w-3/4" /><div className="h-4 bg-gray-200 w-1/2" /></div>
                ) : (
                  <>
                    <div className="space-y-2 text-sm">
                      {rentalItems.length > 0 && (
                        <>
                          <div className="flex justify-between"><span className="text-muted-foreground">{t('cart.daily_total')}</span><span>{rentalDailyTotal.toFixed(2)} EUR</span></div>
                          {multiplier && coefAppliedDaily > 0 && (
                            <div className="flex justify-between"><span className="text-muted-foreground">{days} jour{days > 1 ? 's' : ''} (x{multiplier})</span><span>{(coefAppliedDaily * multiplier).toFixed(2)} EUR</span></div>
                          )}
                          {coefExemptDaily > 0 && (
                            <div className="flex justify-between"><span className="text-muted-foreground">Véhicules — {calendarDays} jour{calendarDays > 1 ? 's' : ''}</span><span>{(coefExemptDaily * (calendarDays || 1)).toFixed(2)} EUR</span></div>
                          )}
                          <div className="flex justify-between"><span className="text-muted-foreground">{t('cart.insurance', 'Assurance')} (8%)</span><span>{insurance.toFixed(2)} EUR</span></div>
                        </>
                      )}
                      {saleItems.length > 0 && (
                        <div className="flex justify-between"><span className="text-muted-foreground">{t('cart.sale_total', 'Achat')}{saleItems.length > 1 ? ` (${saleItems.length})` : ''}</span><span>{saleTotal.toFixed(2)} EUR</span></div>
                      )}
                      {franchiseOpt && datesComplete && (
                        <div className="flex justify-between text-black"><span>Rachat de franchise ({franchiseOpt.label_fr})</span><span>{franchiseTotal.toFixed(2)} EUR</span></div>
                      )}
                      {livraisonOpts._result?.enabled && <div className="flex justify-between text-black"><span>{t('cart.delivery', 'Livraison')}</span><span>{livraisonOpts._result.sur_devis ? t('cart.quote_only', 'Sur devis') : `${livraisonTotal.toFixed(2)} EUR`}</span></div>}
                      {recuperationOpts._result?.enabled && <div className="flex justify-between text-black"><span>{t('cart.pickup', 'Récupération')}</span><span>{recuperationOpts._result.sur_devis ? t('cart.quote_only', 'Sur devis') : `${recuperationTotal.toFixed(2)} EUR`}</span></div>}
                    </div>
                    <Separator />
                    <div className="flex justify-between font-bold text-lg"><span>{t('cart.total_ht')}</span><span className="text-primary" data-testid="cart-total">{total.toFixed(2)} EUR</span></div>
                    <p className="text-xs text-muted-foreground">{t('cart.disclaimer')}</p>
                  </>
                )}
                {error && <div className="flex items-start gap-2 p-3 bg-red-50 text-red-600 text-sm"><AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{error}</span></div>}
                <Button onClick={handleSubmitQuote} className="w-full rounded-none bg-primary hover:bg-primary/90" disabled={submitting || !datesComplete} data-testid="submit-quote-btn">
                  {submitting ? t('cart.submitting') : t('cart.submit')}<ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            {/* ── ARTICLES ── */}
            <Card className="border border-border rounded-none">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg">Articles ({cart.items.length})</CardTitle>
                  <div className="flex items-center gap-2">
                    {cpuCode ? (
                      <div className="flex items-center gap-1.5 bg-primary/10 border border-primary/30 px-2.5 py-1 text-xs font-mono" data-testid="cpu-badge">
                        <Hash className="w-3 h-3 text-primary" />
                        <span className="font-bold text-primary" data-testid="cpu-code-display">{cpuCode}</span>
                        <button onClick={() => { navigator.clipboard.writeText(cpuCode); setCpuCopied(true); setTimeout(() => setCpuCopied(false), 2000); }} className="p-0.5 hover:text-primary transition-all" title="Copier" data-testid="cpu-copy-btn">
                          {cpuCopied ? <Check className="w-3 h-3 text-green-600 animate-[scale-in_0.2s_ease-out]" /> : <Copy className="w-3 h-3" />}
                        </button>
                        {cpuCopied && <span className="text-[10px] text-green-600 font-semibold animate-[fade-in_0.2s_ease-out]">Copié !</span>}
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" className="rounded-none text-xs h-7" onClick={generateCpu} disabled={cpuLoading || cart.items.length === 0} data-testid="generate-cpu-btn">
                        <Hash className="w-3 h-3 mr-1" /> {cpuLoading ? '...' : 'Obtenir un CUP'}
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="rounded-none text-xs h-7 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={clearCart} data-testid="clear-cart-btn">
                      <Trash2 className="w-3 h-3 mr-1" /> {t('cart.clear_cart')}
                    </Button>
                  </div>
                </div>
                {cpuCode && (
                  <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1.5" data-testid="cpu-share-hint">
                    <Hash className="w-3 h-3 mt-0.5 flex-shrink-0 text-primary" />
                    <span>Partagez ce CUP avec un collaborateur pour qu'il retrouve ce panier à l'identique sur son compte.</span>
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {cart.items.map((item) => (
                  <div key={item.product_id} data-testid={`cart-item-${item.product_id}`}>
                    {item.is_kit ? (
                      <KitCartItem item={item} updateQuantity={updateQuantity} removeItem={removeItem} updateKit={updateKit} />
                    ) : (
                      <div className="flex gap-3 pb-3 border-b border-border last:border-0 last:pb-0">
                        <Link to={productUrl({ id: item.product_id, name: item.product_name })} className="flex-shrink-0">
                          <div className="w-14 h-14 bg-surface overflow-hidden">
                            <img
                              src={item.image_url || 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100'}
                              alt={item.product_name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100';
                              }}
                            />
                          </div>
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={productUrl({ id: item.product_id, name: item.product_name })}><h3 className="font-medium text-sm line-clamp-1 hover:text-primary transition-colors">{item.product_name}</h3></Link>
                          <p className="text-xs text-primary mt-0.5">{item.daily_price.toFixed(2)} EUR{isSaleItem(item) ? '' : '/jour'}</p>
                        </div>
                        <div className="flex flex-col items-end justify-between gap-1">
                          <button onClick={() => removeItem(item.product_id)} className="text-muted-foreground hover:text-red-500 transition-colors" data-testid={`remove-item-${item.product_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          <div className="flex items-center border border-border">
                            <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="p-1.5 hover:bg-surface transition-colors" data-testid={`decrease-${item.product_id}`}><Minus className="w-3 h-3" /></button>
                            <span className="px-2 text-xs font-medium">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="p-1.5 hover:bg-surface transition-colors" data-testid={`increase-${item.product_id}`}><Plus className="w-3 h-3" /></button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* ── CHARGER UN CUP ── */}
            <div className="border border-dashed border-border p-4">
              <p className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Hash className="w-3.5 h-3.5 text-primary" /> Charger un panier via Code Unique de Panier (CUP)</p>
              <div className="flex gap-2">
                <Input
                  value={cpuInput}
                  onChange={(e) => { setCpuInput(e.target.value.toUpperCase()); setCpuLoadError(''); setCpuPreview(null); }}
                  placeholder="Ex: A1B2C3D4"
                  className="rounded-none font-mono uppercase text-xs h-8"
                  maxLength={8}
                  data-testid="cpu-input"
                />
                <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => loadCpuCart(cpuInput)} disabled={cpuLoading || cpuInput.length < 4} data-testid="cpu-load-btn">
                  <Search className="w-3 h-3 mr-1" /> Charger
                </Button>
              </div>
              {cpuLoadError && <p className="text-xs text-red-600 mt-1">{cpuLoadError}</p>}
              {cpuPreview && (
                <div className="mt-3 border border-border p-3 text-sm space-y-2 bg-surface">
                  <p className="font-semibold text-xs">{cpuPreview.items.length} article(s) dans le CUP {cpuPreview.code}</p>
                  {cpuPreview.items.slice(0, 4).map((item, i) => {
                    const isUnavailable = item.available === false;
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        {item.image_url && <img src={item.image_url} alt="" className={`w-7 h-7 object-contain bg-white ${isUnavailable ? 'opacity-50' : ''}`} />}
                        <div className="flex-1 min-w-0">
                          <div className={`truncate ${isUnavailable ? 'text-red-600 font-medium' : ''}`}>{item.product_name}</div>
                          {isUnavailable && (
                            <div className="text-[10px] italic text-red-600">Ce produit n'est plus référencé</div>
                          )}
                        </div>
                        <span className="text-muted-foreground">x{item.quantity}</span>
                      </div>
                    );
                  })}
                  {cpuPreview.items.length > 4 && <p className="text-xs text-muted-foreground">+{cpuPreview.items.length - 4} autre(s)</p>}
                  <Button className="rounded-none w-full mt-1" size="sm" onClick={mergeCpuToCart} data-testid="cpu-merge-btn">
                    Ajouter au panier en cours
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Auto-adjust rental dates popup */}
      <Dialog open={!!dateAdjustDialog} onOpenChange={(open) => { if (!open) setDateAdjustDialog(null); }}>
        <DialogContent className="max-w-md rounded-none" data-testid="date-adjust-dialog">
          <DialogHeader>
            <DialogTitle>Dates de location ajustées</DialogTitle>
            <DialogDescription>
              {dateAdjustDialog?.source === 'both'
                ? "Les dates de location ont été ajustées pour correspondre aux dates de livraison et de récupération souhaitées."
                : dateAdjustDialog?.source === 'livraison'
                  ? "Les dates de location ont été ajustées pour correspondre à la date de livraison souhaitée."
                  : "Les dates de location ont été ajustées pour correspondre à la date de récupération souhaitée."}
            </DialogDescription>
          </DialogHeader>

          {dateAdjustDialog && (
            <div className="bg-gray-50 border border-gray-200 p-3 space-y-2 text-sm">
              <div className="flex justify-between items-start gap-3">
                <span className="text-muted-foreground shrink-0">Retrait&nbsp;:</span>
                <span className="text-right">
                  {dateAdjustDialog.oldPickup && dateAdjustDialog.newPickup && dateAdjustDialog.oldPickup.getTime() !== dateAdjustDialog.newPickup.getTime() ? (
                    <>
                      <span className="line-through text-gray-400 block">
                        {format(dateAdjustDialog.oldPickup, 'EEEE d MMM yyyy', { locale: fr })}
                      </span>
                      <strong className="text-primary block">
                        {format(dateAdjustDialog.newPickup, 'EEEE d MMM yyyy', { locale: fr })}
                      </strong>
                    </>
                  ) : (
                    <span>{format(dateAdjustDialog.newPickup, 'EEEE d MMM yyyy', { locale: fr })}</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between items-start gap-3">
                <span className="text-muted-foreground shrink-0">Retour&nbsp;:</span>
                <span className="text-right">
                  {dateAdjustDialog.oldReturn && dateAdjustDialog.newReturn && dateAdjustDialog.oldReturn.getTime() !== dateAdjustDialog.newReturn.getTime() ? (
                    <>
                      <span className="line-through text-gray-400 block">
                        {format(dateAdjustDialog.oldReturn, 'EEEE d MMM yyyy', { locale: fr })}
                      </span>
                      <strong className="text-primary block">
                        {format(dateAdjustDialog.newReturn, 'EEEE d MMM yyyy', { locale: fr })}
                      </strong>
                    </>
                  ) : (
                    <span>{format(dateAdjustDialog.newReturn, 'EEEE d MMM yyyy', { locale: fr })}</span>
                  )}
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setDateAdjustDialog(null)}
              className="rounded-none bg-primary hover:bg-primary/90 text-white"
              data-testid="date-adjust-ok-btn"
            >
              OK, j&apos;ai compris
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CartPage;
